import { Meta, References, Row } from './types';

function url(ajaxUrl: string, action: string, params: Record<string, string> = {}): string {
    const sep = ajaxUrl.includes('?') ? '&' : '?';
    const qs = new URLSearchParams({ ajax: '1', action, ...params }).toString();
    return `${ajaxUrl}${sep}${qs}`;
}

export async function fetchMeta(ajaxUrl: string, entity: string): Promise<Meta> {
    const res = await fetch(url(ajaxUrl, 'Meta', { entity }), { credentials: 'same-origin' });
    const data = await res.json();
    if (!res.ok || data.error) throw new Error(data.error || `HTTP ${res.status}`);
    return data as Meta;
}

export interface RowsPage { rows: Row[]; total: number; }

export interface RowsParams {
    page: number;
    pageSize: number;
    search?: string;
    refColumn?: string;
    refValue?: string | number;
    sort?: string;
    dir?: 'asc' | 'desc';
}

/** One server-side page of rows (replaces the old 1000-row cap). */
export async function fetchRows(ajaxUrl: string, entity: string, p: RowsParams): Promise<RowsPage> {
    const params: Record<string, string> = {
        entity,
        page: String(p.page),
        pageSize: String(p.pageSize),
        search: p.search ?? '',
    };
    if (p.refColumn) {
        params.refColumn = p.refColumn;
        params.refValue = String(p.refValue ?? '');
    }
    if (p.sort) {
        params.sort = p.sort;
        params.dir = p.dir ?? 'desc';
    }
    const res = await fetch(url(ajaxUrl, 'Rows', params), { credentials: 'same-origin' });
    const data = await res.json();
    if (!res.ok || data.error) throw new Error(data.error || `HTTP ${res.status}`);
    return data as RowsPage;
}

async function post(ajaxUrl: string, action: string, body: Record<string, string>): Promise<any> {
    const res = await fetch(url(ajaxUrl, action), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(body).toString(),
    });
    const data = await res.json();
    if (!res.ok || data.error) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
}

export function saveRow(
    ajaxUrl: string,
    entity: string,
    id: string | number | null,
    values: Row,
): Promise<{ id: string | number }> {
    return post(ajaxUrl, 'Save', {
        entity,
        id: id == null ? '' : String(id),
        values: JSON.stringify(values),
    });
}

export function deleteRow(ajaxUrl: string, entity: string, id: string | number): Promise<any> {
    return post(ajaxUrl, 'Delete', { entity, id: String(id) });
}

export interface MaterialSuggestion {
    color?: string | null;
    edge_core_color?: string | null;
    color_tone_id?: number | null;
    id_decor_name?: string | null;
    manufacturer_id?: number | null;
}

/** Fields the Decor form can pre-fill from a chosen material (order = merge order). */
export const MATERIAL_SUGGESTION_FIELDS: (keyof MaterialSuggestion)[] = [
    'color', 'edge_core_color', 'color_tone_id', 'id_decor_name', 'manufacturer_id',
];

/** Derive Decor fields from the chosen material (diffuse avg colour + decor code + manufacturer). */
export function autoColor(ajaxUrl: string, idMaterial: string | number): Promise<MaterialSuggestion> {
    return post(ajaxUrl, 'AutoColor', { id_material: String(idMaterial) });
}

export interface TextureInfo {
    width: number | null;
    height: number | null;
    dpi: number | null;
    dpiSource?: string | null;
    pixelWidth?: number | null;
    pixelHeight?: number | null;
}

/** Measure a CDN texture by path (proxied server-side to inspect_texture.php). */
export function inspectTexture(ajaxUrl: string, path: string): Promise<TextureInfo> {
    return post(ajaxUrl, 'InspectTexture', { path });
}

export interface UploadedTexture {
    name: string | null;        // delivery path for uc_material_diffuse.name
    storedAs: string | null;
    width: number | null;
    height: number | null;
    dpi: number | null;
}

/** Upload an original to the CDN's Materials/<manufacturer>/<collection>/ (proxied; token server-side). */
export async function uploadTexture(
    ajaxUrl: string, file: File, manufacturer: string, collection: string,
): Promise<UploadedTexture> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('manufacturer', manufacturer);
    if (collection) fd.append('collection', collection);
    const res = await fetch(url(ajaxUrl, 'UploadTexture'), { method: 'POST', credentials: 'same-origin', body: fd });
    const data = await res.json();
    if (!res.ok || data.error) throw new Error(data.error || `HTTP ${res.status}`);
    return data as UploadedTexture;
}

export interface GeneratedTexture {
    name: string | null;        // delivery path for uc_material_diffuse.name
    thumb: string | null;
    maps: string[];
    targetDpi: number | null;
    rotated: boolean;
    propagated?: { decors: number; boards: number; edges: number }; // product_image pushed to catalog
    nameUpdated?: string | null; // diffuse.name repointed at the delivery path (C3)
}

/** Build the CDN delivery textures (+ thumbnail) for a material from its Materials/ source. */
export function generateTexture(
    ajaxUrl: string,
    params: { path: string; sourceDpi: number | string; thumb?: boolean; rotate?: boolean; alpha?: string; idDiffuse?: number | string | null },
): Promise<GeneratedTexture> {
    const body: Record<string, string> = { path: params.path, sourceDpi: String(params.sourceDpi) };
    if (params.thumb) body.thumb = '1';
    if (params.rotate) body.rotate = '1';
    if (params.alpha) body.alpha = params.alpha;
    if (params.idDiffuse) body.id_material_diffuse = String(params.idDiffuse); // enables product_image propagation
    return post(ajaxUrl, 'GenerateTexture', body);
}

/** Build the FULL material (diffuse + alpha composite + surface maps + thumbnail) in one CDN call. */
export function generateMaterial(
    ajaxUrl: string,
    params: { idDiffuse: number | string; idSurface?: number | string | null },
): Promise<GeneratedTexture> {
    const body: Record<string, string> = { id_material_diffuse: String(params.idDiffuse) };
    if (params.idSurface) body.id_material_surface = String(params.idSurface);
    return post(ajaxUrl, 'GenerateMaterial', body);
}

export interface BoardDecorDefaults { decor_structure_id: number | null; diffuse_image: string | null; compatible_edgings: string[]; }

/** For the Board form: a decor's default structure + diffuse preview URL. */
export function boardDecorDefaults(ajaxUrl: string, decorId: string | number): Promise<BoardDecorDefaults> {
    return post(ajaxUrl, 'BoardDecorDefaults', { decor_id: String(decorId) });
}

export async function fetchReferences(ajaxUrl: string, entity: string, id: string | number): Promise<References> {
    const res = await fetch(url(ajaxUrl, 'References', { entity, id: String(id) }), { credentials: 'same-origin' });
    const data = await res.json();
    if (!res.ok || data.error) throw new Error(data.error || `HTTP ${res.status}`);
    return data as References;
}
